import React, { useEffect, useRef, useState } from 'react';
import { ContextualMenu, FontIcon, getTheme, Icon, IContextualMenuItem } from '@fluentui/react';
import { getBwjListingLineHeight, useBwjListingStyles } from './styles';
import { BwjLazyImage } from '../bwj-lazy-image';
import { IActionItemProps, IBwjListingBadgeProps, BadgeType } from './entities';
import { ILazyImagePlaceholderStyles } from '../bwj-lazy-image/placeholder';

export interface IBwjListingProps {
  actions?: IActionItemProps[];
  invalid?: boolean;
  selected?: boolean;
  heading: string;
  badge?: IBwjListingBadgeProps;
  loadImage?: boolean;
  onImageLoaded?: (data: any) => void;
  onImageLoadError?: (data: any) => void;
  secondaryLine?: string;
  teritaryLine?: string;
  footnote?: string;
  data?: any;
  onClick?: (data: any) => void;
}

const badgeColors = [
  '#D32F2F',
  '#880E4F',
  '#6A1B9A',
  '#311B92',
  '#283593',
  '#1976D2',
  '#0277BD',
  '#006064',
  '#00695C',
  '#2E7D32',
  '#F57F17',
  '#FF8F00',
  '#FF6D00',
  '#D84315',
  '#4E342E',
  '#455A64'
];

const normalizeBadgeContent = (content: string) => {
  let text = content.trim();

  // enforce exactly two characters
  while(text.length < 2) { text += ' '; }
  text = text.substring(0, 2);

  return text.toUpperCase();
};

enum ActionButtonMode {
  None,
  Single,
  Menu,
}

const smallPlaceholderIcons: ILazyImagePlaceholderStyles = {
  icon: { fontSize: '20px' }
};

export const BwjListing: React.FunctionComponent<IBwjListingProps> = (props) => {

  const { actions, badge, data } = props;
  const [showActionMenu, setShowActionMenu] = useState(false);
  const badgeColorIndex = useRef(0);
  const menuItems = useRef<IContextualMenuItem[]>([]);
  const cover = React.createRef<HTMLDivElement>();
  const actionButtonRef = useRef(null);
  const theme = getTheme();

  // compute badge color if necessary
  useEffect(() => {
    if(!!badge && badge.type !== BadgeType.Icon) {
      let idx = (new Date()).getTime() / 16;
      let badgeText = normalizeBadgeContent(badge.content);
      idx += (badgeText.charCodeAt(0) * 100) + (badgeText.charCodeAt(1) * 10);
      badgeColorIndex.current = Math.floor(idx) % 16;
    }
  }, [badge]);

  const actionButtonMode = !actions || actions.length < 1 ? ActionButtonMode.None :
    actions.length === 1 ? ActionButtonMode.Single : ActionButtonMode.Menu;
  const actionButtonIcon = actionButtonMode === ActionButtonMode.Single ? actions![0].icon ?? 'Unknown' : 'More';

  // derive menu items from actions in props
  useEffect(() => {
    if(actionButtonMode === ActionButtonMode.Menu) {
      menuItems.current = (actions ?? []).map((item, idx) => {
        const onClick = () => {
          !!item.onClick && item.onClick(data);
          setShowActionMenu(false);
        };

        return {
          key: `${idx}`,
          onClick: onClick,
          iconProps: !!item.icon ?  { iconName: item.icon } : undefined,
          text: item.text ?? 'Menu Item',
          title: item.text ?? 'Menu Item',
        };
      });
    }
  }, [actionButtonMode, actions, data]);

  const styles = useBwjListingStyles();

  const getListingInlineStyle = () => {
    let lineCount = 1;
    if(!!props.secondaryLine) { lineCount++; }
    if(!!props.teritaryLine) { lineCount++; }
    if(!!props.footnote) { lineCount++; }

    return { height: getBwjListingLineHeight(lineCount, badge?.type ?? BadgeType.Icon) };
  }

  const getBadgeStyle = () => {
    if(!!badge) {
      if(badge.type === BadgeType.CircularText) {
        return styles.circularBadge;
      }
      else if(badge.type === BadgeType.SemiSquareText) {
        return styles.semiSquareBadge;
      }
      else if(badge.type === BadgeType.Icon) {
        return styles.iconBadge;
      }
      else if(badge.type === BadgeType.Image) {
        return styles.imageBadge;
      }
    }

    return styles.badge;
  }

  const titleStyle = props.invalid ? styles.invalidTitle : styles.title;
  const listingStyle = props.selected ? styles.selectedListing : styles.listing;

  const handleCoverClick: React.FormEventHandler = (e) => {
      if(e.target === cover.current) {
          if(!!props.onClick) {
              props.onClick(props.data);
          }
      }
  };

  const handleButtonClick: React.FormEventHandler = (e) => {
      if(actionButtonMode === ActionButtonMode.Single && !!actions![0].onClick) {
          actions![0].onClick(props.data);
      }
      else {
        setShowActionMenu(true);
      }

      e.preventDefault();
      e.stopPropagation();
  };

  const badgeInlineStyle = {
    backgroundColor: props.badge?.type !== BadgeType.Icon ? badgeColors[badgeColorIndex.current] : undefined,
    color: props.badge?.type === BadgeType.Icon ? props.badge.color ?? theme.palette.themePrimary : undefined
  };

  return (
    <div className={listingStyle} style={getListingInlineStyle()}>
      {!!props.badge &&
      <div className={getBadgeStyle()} style={badgeInlineStyle} aria-hidden="true">
        {props.badge.type === BadgeType.Icon ?
        <FontIcon iconName={props.badge.content} aria-hidden="true" /> :
        props.badge.type === BadgeType.Image ?
        <BwjLazyImage
          src={props.badge.content}
          alt={props.badge.alt}
          loadImage={props.loadImage}
          onImageLoaded={() => !!props.onImageLoaded && props.onImageLoaded(props.data)}
          onLoadError={() => !!props.onImageLoadError && props.onImageLoadError(props.data)}
          styles={{
            untouchedFrame: smallPlaceholderIcons,
            errorFrame: smallPlaceholderIcons,
            loadingFrame: smallPlaceholderIcons,
          }}
        /> :
        normalizeBadgeContent(props.badge.content)}
      </div>}
      <div className={props.badge?.type === BadgeType.Image ? styles.imageContentContainer : styles.contentContainer}>
        <h1 className={titleStyle}>
          {props.invalid && <FontIcon iconName="Warning" aria-hidden="true" />} {props.heading}
        </h1>
        {!!props.secondaryLine ? <h2 className={styles.contentText}>{props.secondaryLine}</h2> : null}
        {!!props.teritaryLine ? <h3 className={styles.contentText}>{props.teritaryLine}</h3> : null}
        {!!props.footnote ? <p className={styles.footnote}>{props.footnote}</p> : null}
      </div>
      <div className={styles.cover} onClick={handleCoverClick} ref={cover}>
          {actionButtonMode !== ActionButtonMode.None &&
          <button ref={actionButtonRef} className={styles.actionButton} onClick={handleButtonClick}>
            <Icon iconName={actionButtonIcon} aria-hidden="true" />
          </button>}
      </div>
      <ContextualMenu
        items={menuItems.current}
        hidden={!showActionMenu}
        target={actionButtonRef}
        onDismiss={() => setShowActionMenu(false)}
      />
    </div>
  );
};
